import { renderApp } from './App/App';
import { Mode } from '../common/types';
import { isVestel2017 } from './utils/isVestel2017';
import { apmRumFilters, apmRumIdPlaceholders } from './utils/apm-utils';
import {
  ElisaDevice,
  HbbTVDevice,
  HisenseDevice,
  PanasonicDevice,
  PhilipsDevice,
  PlayStation4Device,
  PlayStation5Device,
  SonyDevice,
  TeliaDevice,
  TizenDevice,
  VestelDevice,
  WebOSDevice,
  TiVoDevice,
} from './deviceTypes';
import { bindKeysIfNoExist } from './utils/keys';
import adobeAnalytics from './Player/analytics/adobeAnalytics';
import { Features } from './contexts/AppFeaturesContext/AppFeaturesContext';
import { ApplicationResolution, rootFontSize } from './applicationResolution';
import { getPageLoad } from '../server/utils/pageload';
import { setApmServiceLabel } from '../server/elasticApmOptions';
import { apm } from '@elastic/apm-rum';
import { getApplicationSize } from './utils/ui-helpers';
import { isHbbtvLike } from '../common/utils';
import { getUILanguage } from './localizations/language';
import { initializeYleSelva } from '../selva/selva';
import { isOldPanasonic } from './utils/deviceCheck';

const setBodyStyles = (
  applicationResolution: ApplicationResolution,
  mode: Mode
) => {
  const applicationSize = getApplicationSize(applicationResolution);
  const width = isHbbtvLike(mode) ? 1280 : applicationSize.width;
  const height = isHbbtvLike(mode) ? 720 : applicationSize.height;

  document.body.style.width = `${width}px`;
  document.body.style.height = `${height}px`;
};

function init() {
  const {
    VERSION: version,
    PUBLIC_PATH_PREFIX,
    RESOLUTION: applicationResolution,
    MODE: mode,
    E2E_TESTS: e2eTests,
    elasticAPMOptions,
    MUX_ENV_KEY: muxKeyProduction,
    BRAZE_API_KEY: brazeApiKey,
    FEATURE_BRAZE,
  } = window.yleLeanback;

  const language = getUILanguage();

  initializeYleSelva();

  // Correct path for webpack chunks. This environment variable is not available on build time.
  __webpack_public_path__ = `${PUBLIC_PATH_PREFIX}/`;

  document.documentElement.style.fontSize = `${rootFontSize[applicationResolution]}px`;
  setBodyStyles(applicationResolution, mode);

  if (version !== 'dev' && !isVestel2017) {
    const pageLoad = getPageLoad(apm.startTransaction());
    setApmServiceLabel(apm.getCurrentTransaction(), mode);

    const elasticAPMWithPageload = {
      ...elasticAPMOptions,
      ...pageLoad,
      disableInstrumentations: ['xmlhttprequest'],
    };
    window.elasticApm.init(elasticAPMWithPageload);

    window.elasticApm.addFilter(apmRumFilters);
    window.elasticApm.addFilter(apmRumIdPlaceholders);
    window.elasticApm.addLabels({ service: mode });
  }

  const selectDevice = (mode: Mode) => {
    switch (mode) {
      case 'webos':
        return new WebOSDevice();
      case 'tizen':
        return new TizenDevice();
      case 'philips':
        return new PhilipsDevice(window.navigator.userAgent);
      case 'panasonic':
        return new PanasonicDevice(window.navigator.userAgent);
      case 'vestel':
        return new VestelDevice(window.navigator.userAgent);
      case 'telia':
        return new TeliaDevice(window.navigator.userAgent);
      case 'elisa':
        return new ElisaDevice(window.navigator.userAgent);
      case 'hisense':
        return new HisenseDevice();
      case 'tivo':
        return new TiVoDevice(window.navigator.userAgent);
      case 'sony':
        return new SonyDevice(window.navigator.userAgent);
      case 'ps4':
        return new PlayStation4Device(window.navigator.userAgent);
      case 'ps5':
        return new PlayStation5Device(window.navigator.userAgent);
      default: {
        return new HbbTVDevice(window.navigator.userAgent);
      }
    }
  };
  const deviceProps = selectDevice(mode);

  if (isOldPanasonic(deviceProps)) {
    try {
      window.localStorage.removeItem('snowplowOutQueue_sp1');
    } catch (e) {
      console.log('Failed to clear snowplow localStorage on old Panasonic', e);
    }
  }

  adobeAnalytics.initAnalytics();

  bindKeysIfNoExist(mode);

  const features: Features = {
    languageCode: getUILanguage(),
    mode,
    device: deviceProps,
    applicationResolution,
    e2eTests,
    hostname: window.location.hostname,
    remSize: parseInt(getComputedStyle(document.documentElement).fontSize),
    version,
    muxKeyProduction,
    brazeApiKey,
  };

  const rootElement = document.getElementById('root')!;

  const htmlElement = document.documentElement;

  const languageAttributeNames = [
    'xml:lang', // hbbtv
    'lang', // others
  ];

  languageAttributeNames.forEach(attrName => {
    htmlElement.setAttribute(attrName, language);
  });

  renderApp(rootElement, features, FEATURE_BRAZE);

  if (module.hot) {
    module.hot.accept();
  }
}

init();
